import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2, AlertTriangle, XCircle, Info, RotateCw, X } from 'lucide-react';
import { cn } from '../../lib/utils';

const ToastContext = createContext(null);

const ICONS = {
  success: CheckCircle2,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
};

const ACCENT = {
  success: 'text-success',
  error: 'text-danger',
  warning: 'text-warning',
  info: 'text-accent-cyan',
};

/**
 * Global toast system. Wrap the app in <ToastProvider/> and call
 * `const { toast } = useToast()`.
 *
 *   toast.success('Saved')
 *   toast.error('Something broke', { action: { label: 'Retry', onClick } })
 *   toast.rateLimit(retryFn)   // friendly 429 helper
 */
export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timers = useRef(new Map());

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const handle = timers.current.get(id);
    if (handle) {
      clearTimeout(handle);
      timers.current.delete(id);
    }
  }, []);

  const push = useCallback(
    (type, message, opts = {}) => {
      const id = crypto.randomUUID();
      const duration = opts.duration ?? (opts.action ? 8000 : 4500);
      setToasts((prev) => [...prev, { id, type, message, action: opts.action, title: opts.title }]);
      if (duration !== Infinity) {
        timers.current.set(
          id,
          setTimeout(() => dismiss(id), duration)
        );
      }
      return id;
    },
    [dismiss]
  );

  const toast = useMemo(
    () => ({
      success: (msg, opts) => push('success', msg, opts),
      error: (msg, opts) => push('error', msg, opts),
      warning: (msg, opts) => push('warning', msg, opts),
      info: (msg, opts) => push('info', msg, opts),
      /** Friendly rate-limit toast with a retry button (free-tier 429s). */
      rateLimit: (onRetry) =>
        push('warning', 'API limit reached — try again in a moment.', {
          title: 'Slow down a sec',
          duration: Infinity,
          action: onRetry ? { label: 'Retry', onClick: onRetry } : undefined,
        }),
      dismiss,
    }),
    [push, dismiss]
  );

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <div className="pointer-events-none fixed inset-x-0 bottom-0 z-[100] flex flex-col items-center gap-2 p-4 sm:items-end sm:p-6">
        <AnimatePresence initial={false}>
          {toasts.map((t) => {
            const Icon = ICONS[t.type];
            return (
              <motion.div
                key={t.id}
                layout
                initial={{ opacity: 0, y: 24, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.15 } }}
                transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                className="glass-strong pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-xl p-4 shadow-soft"
                role="status"
              >
                <Icon className={cn('mt-0.5 h-5 w-5 shrink-0', ACCENT[t.type])} />
                <div className="min-w-0 flex-1">
                  {t.title && <p className="text-sm font-semibold text-content">{t.title}</p>}
                  <p className="text-sm text-content-muted">{t.message}</p>
                  {t.action && (
                    <button
                      onClick={() => {
                        t.action.onClick();
                        dismiss(t.id);
                      }}
                      className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-brand-500/15 px-2.5 py-1 text-xs font-medium text-brand-300 transition-colors hover:bg-brand-500/25"
                    >
                      <RotateCw className="h-3.5 w-3.5" />
                      {t.action.label}
                    </button>
                  )}
                </div>
                <button
                  onClick={() => dismiss(t.id)}
                  aria-label="Dismiss"
                  className="shrink-0 rounded-md p-1 text-content-subtle transition-colors hover:bg-white/5 hover:text-content"
                >
                  <X className="h-4 w-4" />
                </button>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within <ToastProvider>');
  return ctx;
}
